import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, UntypedFormControl } from "@angular/forms";
import { isEmpty } from "lodash";

import { ContainerRatingsService } from "src/app/cbta-web/ratings/container-ratings-form/services/base/container-ratings.services";
import { MANEUVER_OUTCOME_LABEL } from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-rating-outcome-interaction";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	DraftItem,
	InstructorOtherComments,
	InstructorOtherCommentsReg,
	OBComments,
	OBCommentsReq,
} from "src/app/models/models";

@Component({
	selector: "app-form-summary-modal",
	templateUrl: "./form-summary-modal.component.html",
	styleUrls: ["./form-summary-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FormSummaryModalComponent implements OnInit {
	@Input() comments: OBComments[] = [];
	@Input() instructorComments: InstructorOtherComments[] = [];
	@Input() traineeAssessmentId: string = "";
	/** When true, shows Candidate 1/2 labels (TS-1 v3 dual-column rating only). */
	@Input() showCandidateSlotLabels = false;

	CANDIDATE_ROLE = CONSTANT.CANDIDATE_ROLE;
	readonly maneuverOutcomeLabel = MANEUVER_OUTCOME_LABEL;

	editable: Array<boolean> = [];
	addObComments = new UntypedFormControl("");
	otherInstructorComments = new UntypedFormControl("");
	PFPMToggleForm!: FormGroup;

	constructor(
		private fb: FormBuilder,
		private containerRatingsService: ContainerRatingsService,
	) {}

	ngOnInit(): void {
		this.resetEditable();
		this.createPFPMToggleForm();
	}

	// ---- PF/PM toggle form (unchanged logic) ----
	createPFPMToggleForm(): void {
		this.PFPMToggleForm = new FormGroup({
			PFPMToggles: this.fb.array([]),
		});
		this.updatePFPMToggles();
	}

	updatePFPMToggles(): void {
		this.comments.forEach((item, index) => {
			this.addPFPMToggle(index, item.candidateRole !== CONSTANT.CANDIDATE_ROLE.PF);
		});
	}

	addPFPMToggle(id: number, initialValue: boolean): void {
		this.toggleArray.push(
			this.fb.group({
				id,
				value: initialValue,
			}),
		);
	}

	getToggleControl(idx: number): FormGroup | null {
		const toggles = this.toggleArray.controls as FormGroup[];
		const toggleControl = toggles.find((toggle) => toggle.get("id")?.value === idx);
		return toggleControl || null;
	}

	getToggleValue(id: number): boolean {
		const control = this.getToggleControl(id);
		return control ? control.get("value")?.value : false;
	}

	get toggleArray(): FormArray {
		return this.PFPMToggleForm.get("PFPMToggles") as FormArray;
	}

	onToggleChange(id: number, isChecked: boolean): void {
		const control = this.getToggleControl(id);
		if (control) control.get("value")?.setValue(isChecked);
	}

	// ---- OB comments editing (unchanged logic, storage via service) ----
	editComment(comment: OBComments, index: number): void {
		this.resetEditable();
		this.editable[index] = true;
		this.addObComments.setValue(comment.instructorComment);
	}

	saveComment(comment: OBComments, index: number): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		if (!record) return;
		this.setEditedOBComments("offline", comment, index, record);
	}

	setEditedOBComments(_state: string, comment: OBComments, index: number, record: DraftItem): void {
		const candidateRating = record?.candidateRating ?? {};
		const addRemoveOb = candidateRating?.addRemoveOb ?? [];
		const selectedOBs = candidateRating?.selectedOBs ?? [];

		const role = this.getToggleValue(index) === true ? CandidateRole.PM : CandidateRole.PF;

		if (comment?.ob !== "TEM") {
			selectedOBs.map((item: OBComments) => {
				if (item.ob == comment.ob && item.candidateId == comment.candidateId && item.commentId == comment.commentId) {
					item.instructorComment = this.addObComments.value;
					this.comments[index].instructorComment = this.addObComments.value;
					this.comments[index].candidateRole = role;
					// keep original indexing behavior
					if (selectedOBs[index]) {
						selectedOBs[index].candidateRole = role;
					}
				}
			});
		}

		if (comment?.ob === "TEM") {
			selectedOBs.map((item: OBComments) => {
				if (item.ob == comment.ob && item.candidateId == comment.candidateId) {
					item.instructorComment = this.addObComments.value;
					this.comments[index].instructorComment = this.addObComments.value;
					this.comments[index].candidateRole = role;
					// keep original indexing behavior
					if (selectedOBs[index]) {
						selectedOBs[index].candidateRole = role;
					}
				}
			});
		}

		addRemoveOb.map((item: OBCommentsReq) => {
			if (
				item.maneuverCode == comment.maneuverCode &&
				item.candidateId == comment.candidateId &&
				item.observationCode == comment.observationCode
			) {
				item.description = this.addObComments.value;
				item.candidateRole = role;
			}
		});

		// write via ContainerRatingsService (same payload shape as before)
		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...candidateRating,
				selectedOBs,
				addRemoveOb,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		this.editable[index] = false;
		this.updatePFPMToggles(); // keep original UI refresh
	}

	// ---- Instructor "Other Training & Observations" comments (unchanged logic) ----
	editInstructorComment(comment: InstructorOtherComments, index: number): void {
		this.resetInstructorEditable();
		this.editable[index] = true;
		this.otherInstructorComments.setValue(comment.comment);
	}

	saveInstructorComment(comment: InstructorOtherComments, index: number): void {
		const record: DraftItem | undefined = this.containerRatingsService.draft;
		if (!record) return;
		this.setEditedOtherComments("offline", comment, index, record);
	}

	setEditedOtherComments(_state: string, comment: InstructorOtherComments, index: number, record: DraftItem): void {
		const candidateRating = record?.candidateRating ?? {};
		const otherComments = candidateRating?.otherComments ?? [];

		otherComments.map((item: InstructorOtherCommentsReg) => {
			if (item.candidateId === comment.candidateId) {
				comment.comment = this.otherInstructorComments.value;
				item.comment = this.otherInstructorComments.value;
				// NOTE: original behavior: all entries with same candidateId get same updated text
			}
		});

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...candidateRating,
				otherComments,
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});

		this.editable[index] = false;
	}

	// ---- helpers (unchanged logic) ----
	resetEditable(): void {
		this.editable = Array(this.comments.length).fill(false);
	}

	resetInstructorEditable(): void {
		this.editable = Array(this.instructorComments.length).fill(false);
	}

	isCommentEmpty(): boolean {
		return isEmpty(this.addObComments.value.replace(/\s/g, "")) ? true : false;
	}

	get inDraftMode(): boolean {
		return this.containerRatingsService.inDraftMode;
	}

	getCommentCandidateLabel(comment: OBComments): string {
		return this.candidateSlotLabel(this.candidateIndexForId(comment.candidateId));
	}

	getCommentCandidateLabelClass(comment: OBComments): string {
		const idx = this.candidateIndexForId(comment.candidateId);
		if (idx === 0) return this.showCandidateSlotLabels ? "form-summary-candidate-label--1" : "font-bold text-[#4b4fa6]";
		if (idx === 1) return this.showCandidateSlotLabels ? "form-summary-candidate-label--2" : "font-bold text-[#52a7c2]";
		return "";
	}

	private candidateIndexForId(candidateId: string | undefined): number {
		if (!candidateId) return -1;
		const candidates = this.containerRatingsService.draft?.trainingInformation?.candidates ?? [];
		return candidates.findIndex((c) => c.candidateId === candidateId);
	}

	private candidateSlotLabel(index: number): string {
		if (index < 0) return "";
		if (index === 0) return "Candidate 1";
		if (index === 1) return "Candidate 2";
		return `Candidate ${index + 1}`;
	}

	getOutcomeRateClass(rate: number | undefined): string {
		if (rate === 1) return "text-red";
		if (rate === 2) return "text-yellow";
		if (rate === 3) return "text-yellow-60";
		if (rate === 4) return "text-green";
		if (rate === 5) return "text-green-90";
		return "text-grey-60";
	}
}
